import { useEffect, useState } from "react";
import { Button, Sheet } from "../../../design-v2";
import * as defaultApi from "../api/coachApi";
import { todayKey } from "../domain/coachFormat";
import useCoachMemories from "../hooks/useCoachMemories";
import { ConsentDetails } from "./CoachGate";
import { ArrowRightIcon } from "./coachIcons";
import { MemoryEditor, memoryErrorCopy, MemoryForm, MemoryOverview } from "./MemorySettings";

/*
 * Coach settings: everything that is not the conversation, kept out of it.
 *
 *   Personalization   Memory (summary, add, correct, delete), recall setting
 *   Data controls     consent, export, stop using the coach, erase everything
 *
 * One side sheet with its own stack of screens (the ChatGPT settings
 * structure, in Design V2). Every sensitive operation is a deliberate step on
 * its own screen; nothing destructive is reachable from the thread.
 * `rightsOnly`: the coach is unavailable or not consented to. Memory can still
 * be read and deleted, data exported or erased (rights routes, contract §2.2).
 */

export function downloadJson(data, filename, { documentObject = document, urlApi = URL } = {}) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
  const url = urlApi.createObjectURL(blob);
  const link = documentObject.createElement("a");
  link.href = url;
  link.download = filename;
  link.rel = "noopener";
  documentObject.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => urlApi.revokeObjectURL(url), 1000);
}

const TITLES = Object.freeze({
  root: "Coach settings",
  memory: "Memory",
  "memory-add": "Add to memory",
  "memory-edit": "Edit memory",
  "memory-clear": "Delete all memories",
  consent: "Consent",
  stop: "Stop using the coach",
  erase: "Erase coach data",
});

function Row({ label, value, description, danger = false, onClick }) {
  return (
    <li>
      <button type="button" className={`lz-coach-settings-row${danger ? " lz-coach-settings-row--danger" : ""}`} onClick={onClick}>
        <span className="lz-coach-settings-row__copy">
          <span className="lz-coach-settings-row__label">{label}</span>
          {description ? <span className="lz-coach-settings-row__description">{description}</span> : null}
        </span>
        {value ? <span className="lz-coach-settings-row__value">{value}</span> : null}
        <ArrowRightIcon />
      </button>
    </li>
  );
}

function Group({ title, children }) {
  return (
    <section className="lz-coach-settings-group" aria-label={title}>
      <h3 className="lz-coach-settings-group__title">{title}</h3>
      <ul className="lz-coach-settings-group__rows">{children}</ul>
    </section>
  );
}

function ConfirmScreen({ body, action, busy, error, onConfirm, onCancel }) {
  return (
    <div className="lz-coach-settings__stack">
      <p className="lz-coach-sheet__lead">{body}</p>
      {error ? <p className="lz-coach-inline-error" role="alert">{error}</p> : null}
      <Button variant="danger" className="lz-coach-sheet__wide" isLoading={busy} loadingLabel="Working" onClick={onConfirm}>
        {action}
      </Button>
      <Button variant="ghost" className="lz-coach-sheet__wide" onClick={onCancel}>
        Cancel
      </Button>
    </div>
  );
}

function formatAcceptedAt(iso) {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

export default function CoachSettings({
  open,
  onClose,
  initialScreen = "root",
  home,
  rightsOnly = false,
  api = defaultApi,
  onSettings,
  onDataGone,
  onCountChange,
  onSourceErased,
  onHomeStale,
  onOpenAppSettings,
  download = downloadJson,
}) {
  const [stack, setStack] = useState([{ id: initialScreen }]);
  const [busy, setBusy] = useState(null);
  const [notice, setNotice] = useState(null);
  const memories = useCoachMemories({ api, onCountChange, onSourceErased });
  const { load: loadMemories } = memories;
  const screen = stack[stack.length - 1];
  const canWrite = !rightsOnly;
  const allowRecall = home?.settings?.allowRecall !== false;

  useEffect(() => {
    if (!open) return;
    setStack(initialScreen === "root" ? [{ id: "root" }] : [{ id: "root" }, { id: initialScreen }]);
    setNotice(null);
    setBusy(null);
    loadMemories();
  }, [open, initialScreen, loadMemories]);

  const push = (id, params = {}) => {
    setNotice(null);
    setStack((current) => [...current, { id, ...params }]);
  };
  const back = () => {
    setNotice(null);
    setStack((current) => (current.length > 1 ? current.slice(0, -1) : current));
  };

  async function run(key, task, fallback = "Something went wrong. Try again.") {
    setBusy(key);
    setNotice(null);
    try {
      await task();
    } catch (error) {
      if (["COACH_CONSENT_REQUIRED", "COACH_NOT_AVAILABLE", "COACH_UNAVAILABLE"].includes(error?.code)) onHomeStale?.();
      setNotice({
        tone: "error",
        text: error?.code === "COACH_MESSAGE_IN_PROGRESS" ? "Your coach is still replying. Try again in a moment." : fallback,
      });
    } finally {
      setBusy(null);
    }
  }

  const exportData = () =>
    run("export", async () => {
      const data = await api.getCoachExport();
      download(data, `lyft-zone-coach-${String(data?.exportedAt || "").slice(0, 10) || todayKey()}.json`);
      setNotice({ tone: "success", text: "Your coach data was downloaded." });
    });

  const toggleRecall = () =>
    run("recall", async () => {
      onSettings?.(await api.updateCoachSettings({ allowRecall: !allowRecall }));
    });

  const writeError = (error) => {
    if (!error) return null;
    if (["COACH_CONSENT_REQUIRED", "COACH_NOT_AVAILABLE", "COACH_UNAVAILABLE"].includes(error.code)) onHomeStale?.();
    return memoryErrorCopy(error);
  };

  const activeCount = (memories.memories || []).filter((memory) => memory.status === "ACTIVE").length;
  const editing = screen.id === "memory-edit" ? (memories.memories || []).find((memory) => memory.id === screen.memoryId) : null;

  let content;
  switch (screen.id) {
    case "memory":
      content = (
        <MemoryOverview
          memories={memories.memories}
          status={memories.status}
          canWrite={canWrite}
          onRetry={loadMemories}
          onAdd={() => push("memory-add")}
          onOpen={(memory) => push("memory-edit", { memoryId: memory.id })}
          onDeleteAll={() => push("memory-clear")}
        />
      );
      break;
    case "memory-add":
      content = (
        <MemoryForm
          onCancel={back}
          onSave={async (values) => {
            const message = writeError(await memories.create(values));
            if (!message) back();
            return message;
          }}
        />
      );
      break;
    case "memory-edit":
      content = editing ? (
        <MemoryEditor
          key={editing.id}
          memory={editing}
          canWrite={canWrite}
          onDone={back}
          onSave={async (values) => {
            const patch = {};
            if (values.text !== editing.text) patch.text = values.text;
            if (values.kind !== editing.kind) patch.kind = values.kind;
            if ((values.validUntil || null) !== (editing.validUntil || null)) patch.validUntil = values.validUntil || null;
            const message = Object.keys(patch).length ? writeError(await memories.update(editing.id, patch)) : null;
            if (!message) back();
            return message;
          }}
          onTogglePin={async () => writeError(await memories.update(editing.id, { pinned: !editing.pinned }))}
          onDelete={async (options) => {
            const error = await memories.remove(editing, options);
            if (!error) back();
            return error ? "Couldn't delete. Try again." : null;
          }}
        />
      ) : (
        <p className="lz-coach-sheet__empty">This memory no longer exists.</p>
      );
      break;
    case "memory-clear":
      content = (
        <ConfirmScreen
          body="Your coach forgets everything it remembers about you. Your conversation stays as it is."
          action="Delete all memories"
          busy={busy === "clear"}
          error={notice?.tone === "error" ? notice.text : null}
          onCancel={back}
          onConfirm={() =>
            run("clear", async () => {
              const error = await memories.removeAll();
              if (error) throw error;
              back();
            }, "Couldn't delete. Try again.")
          }
        />
      );
      break;
    case "consent":
      content = (
        <div className="lz-coach-settings__stack">
          <p className="lz-coach-sheet__lead">
            {home?.consent?.acceptedAt ? `You agreed on ${formatAcceptedAt(home.consent.acceptedAt)} to the following.` : "What you agree to when you use the coach."}
          </p>
          <ConsentDetails />
          <p className="lz-coach-sheet__lead">To withdraw it, choose Stop using the coach under Data controls.</p>
        </div>
      );
      break;
    case "stop":
      content = (
        <ConfirmScreen
          body="Your coach stops replying. Your conversation and memories stay until you erase them, and you can agree again later."
          action="Stop using the coach"
          busy={busy === "stop"}
          error={notice?.tone === "error" ? notice.text : null}
          onCancel={back}
          onConfirm={() =>
            run("stop", async () => {
              await api.withdrawCoachConsent();
              onDataGone?.();
            })
          }
        />
      );
      break;
    case "erase":
      content = (
        <ConfirmScreen
          body="This permanently deletes your coach conversation, check-ins and memories, and withdraws your consent. It can't be undone. Your program and Training Profile don't change."
          action="Erase everything"
          busy={busy === "erase"}
          error={notice?.tone === "error" ? notice.text : null}
          onCancel={back}
          onConfirm={() =>
            run("erase", async () => {
              await api.eraseCoachData();
              onDataGone?.();
            })
          }
        />
      );
      break;
    default:
      content = (
        <div className="lz-coach-settings__stack">
          {notice ? (
            <p className={notice.tone === "error" ? "lz-coach-inline-error" : "lz-coach-inline-success"} role={notice.tone === "error" ? "alert" : "status"}>
              {notice.text}
            </p>
          ) : null}
          <Group title="Personalization">
            <Row
              label="Memory"
              value={memories.memories ? (activeCount === 1 ? "1 saved" : `${activeCount} saved`) : null}
              onClick={() => push("memory")}
            />
            {canWrite ? (
              <li>
                <div className="lz-coach-switch-row lz-coach-settings-row lz-coach-settings-row--static">
                  <div className="lz-coach-switch-row__copy">
                    <span className="lz-coach-switch-row__label" id="lz-coach-settings-recall">Search past conversations</span>
                    <span className="lz-coach-switch-row__description">When you ask “do you remember…”, your coach looks through past messages and memories.</span>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-labelledby="lz-coach-settings-recall"
                    aria-checked={allowRecall}
                    className="lz-coach-switch"
                    disabled={busy === "recall"}
                    onClick={toggleRecall}
                  >
                    <span aria-hidden="true" className="lz-coach-switch__thumb" />
                  </button>
                </div>
              </li>
            ) : null}
          </Group>
          <Group title="Data controls">
            {canWrite ? (
              <Row label="Consent" value={home?.consent?.acceptedAt ? `Given ${formatAcceptedAt(home.consent.acceptedAt)}` : null} onClick={() => push("consent")} />
            ) : null}
            <Row label="Export coach data" description="A JSON file of your conversation, check-ins and memories." onClick={busy === "export" ? undefined : exportData} />
            {canWrite ? <Row label="Stop using the coach" onClick={() => push("stop")} /> : null}
            <Row label="Erase coach data" danger onClick={() => push("erase")} />
          </Group>
          {onOpenAppSettings ? (
            <Group title="App">
              <Row label="App settings" onClick={onOpenAppSettings} />
            </Group>
          ) : null}
        </div>
      );
  }

  return (
    <Sheet open={open} onClose={onClose} side="right" aria-label="Coach settings" showCloseButton={false} surfaceClassName="lz-coach-sheet lz-coach-settings">
      <div className="lz-coach-settings__header">
        {stack.length > 1 ? (
          <button type="button" className="lz-coach-icon-button" aria-label="Back" onClick={back}>
            <span aria-hidden="true" className="lz-coach-settings__back">‹</span>
          </button>
        ) : (
          <span />
        )}
        <h2 className="lz-coach-settings__title">{TITLES[screen.id] || TITLES.root}</h2>
        <button type="button" className="lz-coach-icon-button" aria-label="Close coach settings" onClick={onClose}>
          <span aria-hidden="true">×</span>
        </button>
      </div>
      {content}
    </Sheet>
  );
}
